import React, { useEffect, useState } from 'react';

import { Select } from '@aline/common';
import clsx from 'clsx';
import scrollIntoView from 'smooth-scroll-into-view-if-needed';
import { AlineInput } from 'src/AlineInput';
import type { ExistingCollaboratorOption } from 'src/components';
import { Spacer, useHandleAddCollaborator, WorkflowCollaboratorsChooser } from 'src/components';
import { DragIcon } from 'src/components/icons/DragIcon';
import type { WorkflowAssignmentOption } from 'src/components/workflow/WorkflowAssignmentAutocomplete';
import { Apollo } from 'src/gql/generated';
import type { ActionType } from 'src/hooks';
import { useCreateActionType } from 'src/hooks';
import { getIsPlaceholderPermission } from 'src/types/apollo/workflow-permission';
import type { ReactSetStateAction } from 'src/types/general';
import type { WorkflowStep } from 'src/workflow';
import {
  NewDeleteActionButton,
  NewStepHeader,
  useStepStyles,
  workflowCollaboratorChooserWidthPx,
} from 'src/workflow';
import { makeStyles } from 'tss-react/mui';

import { ActionDropdown } from './action/ActionDropDown';

const useStyles = makeStyles()(theme => ({
  autocomplete: {
    width: workflowCollaboratorChooserWidthPx,
  },
  card: {
    paddingLeft: theme.spacing(3),
  },
  cardHeader: {
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    gap: theme.spacing(1),
    height: '100%',
    padding: theme.spacing(0.5, 2),
  },
  dragIcon: {
    opacity: 0,
  },
  firstStep: {
    marginTop: theme.spacing(2),
  },
  nameInput: {
    width: 160,
  },
  stepContent: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2, 0),
  },
}));

interface NewStepProps {
  sectionSteps: WorkflowStep[];
  setNewStepActive: ReactSetStateAction<boolean>;
  workflowId: UUID;
  showProgress?: boolean;
}

export function NewStep({ sectionSteps, setNewStepActive, workflowId }: NewStepProps): JSX.Element {
  const { classes } = useStyles();
  const [ref, setRef] = useState<HTMLDivElement | null>(null);
  const { handleAddCollaborator } = useHandleAddCollaborator(workflowId);
  const { classes: stepClasses } = useStepStyles({ isOver: false, isStepEditable: true });
  const [collaborator, setCollaborator] = useState<ExistingCollaboratorOption | null>(null);
  const [recipientName, setRecipientName] = useState('');
  const { chosenAction, creatingAction, onSetChosenAction } = useNewWorkflowStep(
    sectionSteps,
    workflowId,
    Apollo.ActionPlacement.AfterStep,
    collaborator
  );
  const isFirstStep = sectionSteps.length === 0;

  // Scroll to the new step when it is created
  useEffect(() => {
    if (ref) scrollIntoView(ref, { behavior: 'smooth', scrollMode: 'if-needed' });
  }, [ref]);

  function clearState(): void {
    setCollaborator(null);
    setRecipientName('');
    setNewStepActive(false);
  }

  // Returns the display name to auto-fill when the user picks an existing person.
  // Returns '' for roles and nameless guests (whose displayName is just their
  // email) so the name field is left blank instead of pre-filled with an email.
  function getAutoName(value: WorkflowAssignmentOption): string {
    switch (value.__typename) {
      case 'WorkflowPermission': {
        const { account } = value;
        if (account?.__typename !== 'User') return '';
        return account.displayName === account.email ? '' : account.displayName;
      }
      case 'User':
        return value.displayName === value.email ? '' : value.displayName;
      default:
        return '';
    }
  }

  return (
    <>
      <div
        ref={setRef}
        className={clsx(isFirstStep && classes.firstStep)}
        data-cy={Select.workflow.detail.step.container}
      >
        <div className={stepClasses.step}>
          <div className={classes.stepContent}>
            <NewStepHeader stepNumber={sectionSteps.length + 1} />
            <div className={classes.card}>
              <div className={classes.cardHeader}>
                <DragIcon height={16} opacity={0.5} width={16} />
                <div className={classes.autocomplete}>
                  <WorkflowCollaboratorsChooser
                    emailOnly
                    dataCy={Select.workflow.detail.steps.action.newStepAutocomplete}
                    disabledReason={creatingAction ? 'Creating action...' : ''}
                    value={collaborator}
                    workflowId={workflowId}
                    onChange={async (_, value) => {
                      if (!value) return;

                      // Always set (including '') so picking a role clears any previously typed name.
                      setRecipientName(getAutoName(value));

                      if (value.__typename === 'WorkflowPermission') {
                        setCollaborator({ ...value, actions: [] });
                        return;
                      }

                      const collaborator = await handleAddCollaborator(value);
                      if (collaborator) setCollaborator(collaborator);
                    }}
                  />
                </div>
                {collaborator && !getIsPlaceholderPermission(collaborator) && (
                  <AlineInput
                    className={classes.nameInput}
                    fullWidth={false}
                    placeholder="Name"
                    value={recipientName}
                    onChange={e => setRecipientName(e.target.value)}
                  />
                )}
                <Spacer />
                {collaborator && (
                  <ActionDropdown
                    assignee={{
                      assignedRoleId: collaborator?.role?.id ?? null,
                      assignedUserId:
                        collaborator?.account?.__typename === 'User'
                          ? collaborator.account.id
                          : null,
                      isLauncher: collaborator.isLauncher,
                    }}
                    chosenAction={chosenAction}
                    disabled={creatingAction}
                    workflowId={workflowId}
                    onSetChosenAction={async action => {
                      // || (not ??) so that an empty string is treated as no name.
                      const nameToSave = getIsPlaceholderPermission(collaborator)
                        ? null
                        : recipientName || null;
                      await onSetChosenAction(action, nameToSave);
                      clearState();
                    }}
                  />
                )}
                <NewDeleteActionButton onClick={clearState} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export function useNewWorkflowStep(
  sectionSteps: WorkflowStep[],
  workflowId: UUID,
  actionPlacement: Apollo.ActionPlacement,
  collaborator: ExistingCollaboratorOption | null
) {
  const [addCollabOpen, setAddCollabOpen] = useState<boolean>(false);
  const { createAction, loading: creatingAction } = useCreateActionType();
  const [chosenAction, setChosenAction] = useState<ActionType | null>(null);

  async function onSetChosenAction(actionType: ActionType, recipientName?: string | null) {
    setChosenAction(actionType);
    if (collaborator)
      await createAction({
        actionPlacement,
        actionType,
        assignedRoleId: collaborator?.role?.id ?? null,
        assignedUserId:
          collaborator?.account?.__typename === 'User' ? collaborator.account.id : null,
        email: null,
        isLauncher: collaborator.isLauncher,
        recipientName,
        targetStepId: sectionSteps.at(-1)?.id ?? null,
        workflowId,
      });
  }

  return {
    addCollabOpen,
    chosenAction,
    creatingAction,
    onSetChosenAction,
    setAddCollabOpen,
    setChosenAction,
  };
}
